<template>
	<Row :gutter="[16, 8]">
		<Col>
			<TypographyTitle :level="3" class="pd-vertical">{{ $t('dashboard.userPulseLogs.header') }}</TypographyTitle>
			<TablePaginationToolbar
				ref="paginationToolbarRef"
				v-model:items="filteredEvents"
				v-model:pageSize="pageSize"
				v-model:currentPage="currentPage"
				show-quick-jumper
			/>
		</Col>
	</Row>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0" >
			<Spin :spinning="isLoading">
				<div v-bind="containerProps" class="log-container">
					<div v-bind="wrapperProps" v-if="list.length > 0">
						<UserPulseLogItem v-for="item in list" :key="item.id" :item="item" :parseItems="parseItems" />
					</div>
					<div v-else>
						<Empty :image="simpleImage" :description="$t('dashboard.userPulseLogs.noEvents')" />
					</div>
				</div>
			</Spin>
		</Col>
		<Col :span="6" :offset="0" >
			<VerticalLayout>
				<Affix :offsetTop="77" :target="() => wrapper">
					<Card :bordered="false">
						<VerticalLayout size="middle">
							<DateRangeChooser v-model="datesRange" showTime @change="eventsGet()" quickPeriod="minute5"/>
							<LabeledSwitch
								v-model="parseItems"
								:rightLabel="$t('dashboard.userPulseLogs.parseItems')"
							/>
							<LabeledSwitch
								v-model="showOnlyErrors"
								:rightLabel="$t('dashboard.userPulseLogs.showOnlyErrors')"
							/>
							<UserAddrSessFilter
								ref="userAddrSessFilterRef"
								:items="events"
								addressField="addr"
								usernameField="user"
								sessionIdField="ID"
								:onChange="resetPagination"
							/>
							<Descriptions :column="1">
								<DescriptionsItem :label="$t('dashboard.userPulseLogs.totalEvents')">{{ events.length }}</DescriptionsItem>
								<DescriptionsItem :label="$t('dashboard.userPulseLogs.filteredEvents')">{{ filteredEvents.length }}</DescriptionsItem>
								<DescriptionsItem :label="$t('dashboard.userPulseLogs.totalErrors')">{{ totalErrors }}</DescriptionsItem>
							</Descriptions>
						</VerticalLayout>
					</Card>
				</Affix>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { Row, Col, Card, Affix, Descriptions, DescriptionsItem, Empty, Spin, TypographyTitle } from 'ant-design-vue';

import Api from '@/api/Api';

import { useVirtualList } from '@vueuse/core';

import { dayJsDatetimeToString } from '@/components/common/dateUtils';

import { useProvideInjectWrapper } from '@/utils/useProvideInject';

import UserPulseLogItem from '@/components/dashboard/userPulse/UserPulseLogItem.vue';
import { isErrorEvent } from '@/components/dashboard/userPulse/casterEventUtils';
import { showErrorMessage } from '@/components/common/errorMessage';

import VerticalLayout from '@/components/common/VerticalLayout';
import DateRangeChooser from '@/components/common/DateRangeChooser';
import LabeledSwitch from '@/components/common/LabeledSwitch';
import UserAddrSessFilter from '@/components/common/UserAddrSessFilter.vue';
import TablePaginationToolbar from '@/components/common/TablePaginationToolbar.vue';


export default {
	components: {
		Row, Col, Card, Affix, Descriptions, DescriptionsItem, Empty, Spin, TypographyTitle,
		VerticalLayout, LabeledSwitch, DateRangeChooser, UserAddrSessFilter,
		UserPulseLogItem, TablePaginationToolbar,
	},
	setup(props, { expose }) {
		const isLoading = ref(true);
		const parseItems = ref(true);
		const showOnlyErrors = ref(false);
		const events = ref([]);
		const datesRange = ref();
		const currentPage = ref(1);
		const pageSize = ref(500);
		const resetPagination = () => {
			currentPage.value = 1;
		};
		watch(showOnlyErrors, () => {
			resetPagination();
		});

		const userAddrSessFilterRef = ref();

		const datesRangeStr = computed(() => {
			if (datesRange.value == null || datesRange.value.length < 2 || datesRange.value[0] == null || datesRange.value[1] == null) {
				return null;
			}
			return datesRange.value.map(dayJsDatetimeToString);
		});

		const filteredEvents = computed(() => {
			if (userAddrSessFilterRef.value == null) {
				return [];
			}
			const base = userAddrSessFilterRef.value.filteredItems || [];
			return base.filter(item => showOnlyErrors.value ? isErrorEvent(item) : true);
		});
		const paginationToolbarRef = ref();
		const paginatedFilteredEvents = computed(() => paginationToolbarRef.value != null ? paginationToolbarRef.value.paginatedItems : []);
		const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(paginatedFilteredEvents, { itemHeight: 22 });
		const totalErrors = computed(() => filteredEvents.value.filter(item => isErrorEvent(item)).length);

		const eventsGet = () => {
			if (datesRangeStr.value != null) {
				isLoading.value = true;
				Api.statsCasterEventsListGet(
					datesRangeStr.value[0],
					datesRangeStr.value[1],
					response => {
						isLoading.value = false;
						if (response.status === 'ok') {
							events.value = response.events.map(event => ({ ...JSON.parse(event.raw_data), id: event.id }));
							resetPagination();
							scrollTo(0);
						} else {
							showErrorMessage(response.errors);
						}
					}
				);
			}
		};
		onMounted(eventsGet);

		const { injectValue } = useProvideInjectWrapper;
		const wrapper = injectValue();

		const setSessionIdFilter = sessionId => {
			if (userAddrSessFilterRef.value != null) {
				userAddrSessFilterRef.value.setSessionIdOnly(sessionId);
			}
		};

		expose({
			setSessionIdFilter,
		});

		return {
			isLoading,
			currentPage,
			pageSize,
			parseItems,
			showOnlyErrors,
			userAddrSessFilterRef,
			eventsGet,
			resetPagination,
			datesRange,
			events,
			totalErrors,
			paginationToolbarRef,
			filteredEvents,
			list,
			containerProps,
			wrapperProps,
			wrapper,
			simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
		};
	}
}
</script>

<style scoped>
.log-container {
	border: 1px solid #d9d9d9;
	padding: 10px;
	height: 600px;
	background: white;
}
</style>
